import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Pill } from "./ExecPhase2";
import { InfoTip, MathRow } from "./InfoTip";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => (n == null ? "—" : Number(n).toLocaleString());
const usd = (n) => `$${Math.round(n || 0).toLocaleString()}`;
// NEW badges auto-hide after Wed Oct 14, 2026 (5 days after launch).
const NEW_UNTIL = new Date(2026, 9, 15);
const New = () => new Date() >= NEW_UNTIL ? null : <span className="text-[9px] font-extrabold bg-violet-600 text-white rounded-full px-1.5 py-0.5">NEW</span>;

const Mini = ({ title, info, children, tid, className = "" }) => (
  <Card className={`border-0 shadow-md px-4 py-3 ${className}`} data-testid={tid}>
    <div className="flex items-center justify-between text-[11px] font-bold text-gray-500 uppercase tracking-wide">
      <span className="inline-flex items-center gap-1">{title}{info}</span><New />
    </div>
    {children}
  </Card>
);

const OccHalf = ({ label, pct, ly, sub, bar, tid, full, proj }) => (
  <div data-testid={tid}>
    <div className={`text-[10px] font-bold ${bar.text}`}>{label} <span className="text-gray-400 font-semibold">· MTD</span></div>
    <div className="text-2xl font-extrabold text-gray-900">{pct == null ? "—" : `${pct}%`}</div>
    <div className="h-1.5 bg-gray-100 rounded"><div className={`h-full rounded ${bar.bg}`} style={{ width: `${Math.min(100, pct || 0)}%` }} /></div>
    <div className="text-[10px] text-gray-500 mt-0.5">
      {ly != null && pct != null && <b className={pct >= ly ? "text-emerald-600" : "text-rose-600"}>{pct >= ly ? "▲" : "▼"} {Math.abs(Math.round(pct - ly))} pts vs LY · </b>}{sub}
    </div>
    {full != null && <div className="text-[10px] text-gray-500" data-testid={`${tid}-full`}>Full month on the books: <b className="v3-num text-gray-800">{full}%</b></div>}
    {proj != null && <div className="text-[10px] text-gray-500" data-testid={`${tid}-projected`}>Projected: <b className="v3-num text-[#1F8A7C]">{proj}%</b> if pace continues</div>}
  </div>
);

const OccInfo = ({ o }) => (
  <InfoTip title={`Occupancy · ${o.month_name || "this month"}`} tid="info-occupancy">
    <p><b>MTD</b> = month to date: only nights from the 1st through today. <b>Full month on the books</b> = every night already booked for the whole month (incl. future) ÷ suites × days in month — it matches MTD once the month ends.</p>
    <p><b>Projected</b> = nights already done + nights on the books for the rest of the month + the late bookings you usually get for those days (how much last year's same days grew after this point in the month).</p>
    <p><b>Boarding</b> = average dogs boarding each night so far this month ÷ your suites.</p>
    <MathRow label="Avg dogs a night" value={o.boarding_avg ?? "—"} /><MathRow label="Suites" value={o.runs ?? "—"} />
    <MathRow label="Boarding occupancy" value={o.boarding_pct != null ? `${o.boarding_pct}%` : "—"} strong />
    <p className="pt-1"><b>Daycare</b> = average dogs a day on days you were open ÷ your daily daycare goal.</p>
    <MathRow label="Avg dogs a day" value={o.daycare_avg ?? "—"} /><MathRow label="Daily goal" value={o.daycare_goal ?? "—"} />
    <MathRow label="Daycare occupancy" value={o.daycare_pct != null ? `${o.daycare_pct}%` : "—"} strong />
    <p className="text-gray-500 pt-1">"pts vs LY" = this month's % minus the same month last year. Suites and goal come from Occupancy settings.</p>
  </InfoTip>
);

// Exec Dashboard 2.0 Business Pulse: active customers, repeat rate, open leads, month occupancy split.
export const ExecKpiRow2 = () => {
  const [s, setS] = useState(null);
  const [leads, setLeads] = useState(null);
  useEffect(() => {
    const load = () => {
      axios.get(`${API}/exec-v2/summary`).then((r) => setS(r.data)).catch(() => setS((x) => x || {}));
      axios.get(`${API}/leads/stats/follow-up`).then((r) => setLeads(r.data)).catch(() => setLeads((x) => x || {}));
    };
    load();
    const id = setInterval(load, 300000);
    window.addEventListener("exec:refresh", load);
    return () => { clearInterval(id); window.removeEventListener("exec:refresh", load); };
  }, []);
  const o = s?.occupancy || {};
  const sg = s?.segments || {};
  return (
    <div className="space-y-3"><Pill>Business Pulse</Pill>
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="exec-kpi-row2">
      <Mini title="Active customers" tid="kpi-active-customers" info={
        <InfoTip title="Active customers" tid="info-active-customers">
          <p>Same groups as the <b>Customer Intel</b> tab, based on each owner's last visit in Gingr:</p>
          <MathRow label="Active · last 30 days" value={fmt(sg.active)} strong />
          <MathRow label="Cooling · 31–60 days" value={fmt(sg.cooling)} /><MathRow label="At-Risk · 61–90 days" value={fmt(sg.at_risk)} />
          <p className="text-gray-500">Customer list syncs from Gingr nightly.</p>
        </InfoTip>}>
        <div className="text-2xl font-extrabold text-gray-900" data-testid="kpi-active-value">{fmt(sg.active)}</div>
        <div className="text-[11px] text-gray-500">Cooling {fmt(sg.cooling)} · At-Risk {fmt(sg.at_risk)}</div>
      </Mini>
      <Mini title="Repeat rate" tid="kpi-repeat-rate" info={
        <InfoTip title="Repeat rate" tid="info-repeat-rate">
          <p>Of the customers who visited in the last 30 days, how many had been here before (more than 1 visit all-time).</p>
          <MathRow label="Repeat customers" value={fmt(s?.repeat_customers)} /><MathRow label="Visited last 30 days" value={fmt(s?.month_customers)} />
          <MathRow label="Repeat rate" value={s?.repeat_rate != null ? `${s.repeat_rate}%` : "—"} strong />
        </InfoTip>}>
        <div className="text-2xl font-extrabold text-gray-900">{s?.repeat_rate == null ? "—" : `${s.repeat_rate}%`}</div>
        <div className="text-[11px] text-gray-500">of last-30-day customers came before</div>
      </Mini>
      <Mini title="Open leads" tid="kpi-open-leads" info={
        <InfoTip title="Open leads" tid="info-open-leads">
          <p>Leads still New, Contacted or Qualified (not converted or lost), plus converted leads with a high score that still need a check-in.</p>
          <MathRow label="Never contacted" value={fmt(leads?.urgent)} /><MathRow label="Last contact 2+ days ago" value={fmt(leads?.warning)} />
          <MathRow label="Contacted in last 2 days" value={fmt(leads?.queue)} /><MathRow label="Open leads" value={fmt(leads?.total_needs_followup)} strong />
        </InfoTip>}>
        <div className="text-2xl font-extrabold text-gray-900">{fmt(leads?.total_needs_followup)}</div>
        <Link to="/follow-up-queue" className="text-[11px] font-bold text-rose-600 hover:underline" data-testid="kpi-open-leads-link">{fmt(leads?.urgent)} never contacted → Queue</Link>
      </Mini>
      <Mini title={`Occupancy · ${o.month_name || "this month"}`} tid="kpi-occupancy-month" className="col-span-2" info={<OccInfo o={o} />}>
        <div className="grid grid-cols-2 gap-3 mt-1">
          <OccHalf label="BOARDING" pct={o.boarding_pct} ly={o.boarding_ly} bar={{ bg: "bg-indigo-500", text: "text-indigo-600" }} tid="kpi-occ-boarding" full={o.boarding_full_pct} proj={o.boarding_projected_pct}
            sub={o.boarding_avg != null ? `avg ${Math.round(o.boarding_avg)} dogs a night${o.runs ? ` · ${o.runs} suites` : ""}` : ""} />
          <OccHalf label="DAYCARE" pct={o.daycare_pct} ly={o.daycare_ly} bar={{ bg: "bg-emerald-500", text: "text-emerald-600" }} tid="kpi-occ-daycare" full={o.daycare_full_pct}
            sub={o.daycare_avg != null ? `avg ${Math.round(o.daycare_avg)} dogs a day${o.daycare_goal ? ` · goal ${o.daycare_goal}` : ""}` : ""} />
        </div>
      </Mini>
    </div></div>
  );
};

// MTD pace = so far + rest of LY month × 3-month growth; YTD pace = so far + LY rest of year × YTD growth.
const PaceInfo = ({ p, tid }) => (
  <InfoTip title={p.kind === "mtd" ? "How the month pace is worked out" : "How the year pace is worked out"} tid={tid}>
    <MathRow label={p.kind === "mtd" ? "This month so far" : "This year so far"} value={usd(p.soFar)} />
    <MathRow label={p.kind === "mtd" ? `Last year, rest of ${p.monthName}` : `Last year, rest of year (${p.restLabel})`} value={usd(p.lyRest)} />
    <MathRow label={p.kind === "mtd" ? "× growth, last 3 full months" : "× growth this year to date"} value={`× ${p.growth.toFixed(2)}`} />
    <MathRow label="= Expected rest" value={usd(p.lyRest * p.growth)} />
    <MathRow label="On pace for" value={usd(p.value)} strong />
    {p.lyFull ? <MathRow label={`Last year full ${p.kind === "mtd" ? "month" : "year"}`} value={usd(p.lyFull)} /> : null}
    <p className="text-gray-500 pt-1">{p.kind === "mtd" ? `Growth = last 3 full months combined vs the same months last year (${p.growthLabel}).` : "Growth = this year so far vs the same dates last year."} Using last year's shape for the rest keeps seasonality in the forecast.</p>
  </InfoTip>
);

// "On pace for…" line for MTD/YTD cards.
export const PaceLine = ({ pace, label, tid }) => pace?.value ? (
  <div className="mt-2 pt-2 border-t border-gray-100 text-xs flex items-center gap-1 flex-wrap" data-testid={tid}>
    <New /> <b className="text-gray-900">On pace for {usd(pace.value)}</b> {label}
    {pace.vsLy != null && <b className={pace.vsLy >= 0 ? "text-emerald-600" : "text-rose-600"}>{pace.vsLy >= 0 ? "+" : ""}{pace.vsLy}% vs last yr</b>}
    <PaceInfo p={pace} tid={`${tid}-info`} />
  </div>
) : null;

// Dogs today: booked (existing numbers) | in building now (same source as Daily Ops).
export const DogsTodayCard = ({ occ, change }) => {
  const [tv, setTv] = useState(null);
  useEffect(() => {
    const load = () => axios.get(`${API}/ops/todays-view`).then((r) => setTv(r.data?.summary || {})).catch(() => setTv({}));
    load();
    const id = setInterval(load, 90000);
    return () => clearInterval(id);
  }, []);
  const ib = tv?.breakdown_live?.in_building || {};
  return (
    <Card className="border-0 shadow-md p-5" data-testid="kpi-dogs-today">
      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1">Dogs today
        <InfoTip title="Dogs today" tid="info-dogs-today">
          <p><b>Booked today</b> = every dog on today's Gingr schedule (boarding + daycare), cancellations excluded. "vs LY" compares with the same date last year.</p>
          <p><b>In building</b> = dogs checked in right now (same live number as Daily Ops), refreshed every 90 seconds. "Still to check in" = booked arrivals not checked in yet.</p>
        </InfoTip>
      </div>
      <div className="grid grid-cols-2 gap-3 mt-2">
        <div>
          <div className="text-[10px] font-bold text-gray-500">BOOKED TODAY</div>
          <div className="text-3xl font-bold text-gray-900" data-testid="kpi-dogs-booked">{occ.total_active || 0}</div>
          <div className="text-[11px] text-gray-500">Board {occ.boarding || 0} · Daycare {occ.daycare || 0}</div>
          {change != null && <div className={`text-[11px] font-bold ${change >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{change >= 0 ? "↑" : "↓"} {Math.abs(change)}% vs LY</div>}
        </div>
        <div className="border-l border-gray-100 pl-3">
          <div className="text-[10px] font-bold text-violet-600 flex items-center gap-1">IN BUILDING <New /></div>
          <div className="text-3xl font-bold text-violet-700" data-testid="kpi-dogs-inbuilding">{tv ? (tv.in_building_live ?? "—") : "…"}</div>
          <div className="text-[11px] text-gray-500">Board {ib.boarding?.total ?? 0} · Daycare {ib.daycare?.total ?? 0}</div>
          {tv?.arrivals_live != null && <div className="text-[11px] text-gray-500">{tv.arrivals_live} still to check in</div>}
        </div>
      </div>
    </Card>
  );
};
